import { QueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, redirect } from '@tanstack/react-router';

import { useBedrijf } from '@/api/queries/bedrijf';
import { BedrijfCardLink } from '@/components/BedrijfCard/BedrijfCard';
import { Disclaimer } from '@/components/Disclaimer/Disclaimer';
import { PaginationLinks } from '@/components/PaginationLinks/PaginationLinks';
import { SearchParams } from '@/routes';

import { createAddressBedrijfFilter } from '../utils/createAddressBedrijfFilters';
import { searchRouteApi } from '../utils/searchRouteApi';

const PAGE_SIZE = 10;

/**
 * Query Options
 */
const bedrijfQueryOptions = (search: SearchParams) => {
  const filter = createAddressBedrijfFilter(search);

  filter.push({
    field: 'Volgend',
    op: 'is_null',
    value: '',
  });

  return useBedrijf.getOptions({
    page: search.p,
    size: PAGE_SIZE,
    filter,
    sort: [
      {
        direction: 'asc',
        field: 'Naam',
      },
    ],
  });
};

/**
 * Preload function
 */
export const preloadSearchBedrijfView = async ({
  queryClient,
  search,
}: {
  queryClient: QueryClient;
  search: SearchParams;
}) => {
  const bedrijven = await queryClient.ensureQueryData(
    bedrijfQueryOptions(search)
  );
  if (!bedrijven.num_pages) return;
  if (bedrijven.num_pages < search.p) {
    throw redirect({ to: '/', search: { p: bedrijven.num_pages } });
  }
};

/**
 * View
 */
export const SearchBedrijfView = () => {
  const search = searchRouteApi.useSearch();

  const bedrijven = useSuspenseQuery(bedrijfQueryOptions(search));

  return (
    <div className="container-default mt-3">
      <ul className="flex flex-col gap-1">
        {!bedrijven.data?.objects?.length ? (
          <li>
            <p className="style-body italic text-gray-800">
              Deze zoekterm(en) hebben geen resultaten opgeleverd.
            </p>
          </li>
        ) : (
          <>
            {bedrijven.data.objects.map((bedrijf) => {
              return (
                <li key={bedrijf.ID}>
                  <BedrijfCardLink
                    to="/$bedrijfLineageId/bedrijf/overzicht"
                    params={{
                      bedrijfLineageId: bedrijf.Lineage,
                    }}
                    title={bedrijf.Naam ?? ''}
                    location={`${bedrijf.Straatnaam} ${bedrijf.Huisnummer}, ${bedrijf.Postcode} ${bedrijf.Plaatsnaam}`}
                    number={bedrijf.Locatienummer}
                    tenant={bedrijf.Tenant}
                  />
                </li>
              );
            })}

            {(bedrijven.data?.num_pages ?? 0) > 1 ? (
              <div className="mt-3 flex justify-center">
                <PaginationLinks
                  aria-label="Bedrijven pagina's"
                  renderLink={({ children, page }) => (
                    <Link to="/" search={(prev) => ({ ...prev, p: page })}>
                      {children}
                    </Link>
                  )}
                  currentPage={search.p}
                  totalPages={bedrijven.data?.num_pages ?? 0}
                />
              </div>
            ) : null}
          </>
        )}
      </ul>

      <Disclaimer className="mt-4 px-4" />
    </div>
  );
};
